// 학부모 설정 페이지

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import InfoIcon from '@mui/icons-material/Info';
import HelpIcon from '@mui/icons-material/Help';
import LogoutIcon from '@mui/icons-material/Logout';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useSupabaseParentAuth } from '../hooks/useSupabaseParentAuth';
import { getAccessToken } from '../../../lib/authUtils';

export default function SettingsPage() {
  const { parent, signOut } = useSupabaseParentAuth();

  // 알림 설정 상태 (UI only - 실제 연동은 나중에)
  const [notifications, setNotifications] = useState({
    entryExit: true,
    kakaoAlimtalk: true,
    lateAlert: false,
    absenceAlert: true,
  });

  // 다이얼로그 상태
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);

  // 스낵바 상태
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // 비밀번호 변경 폼
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // 전화번호 변경 폼
  const [phoneForm, setPhoneForm] = useState({
    newPhone: '',
    verificationCode: '',
  });

  const handleNotificationChange = (key: keyof typeof notifications) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
    setSnackbar({ open: true, message: '설정이 저장되었습니다.', severity: 'success' });
  };

  const [passwordLoading, setPasswordLoading] = useState(false);

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setSnackbar({ open: true, message: '새 비밀번호가 일치하지 않습니다.', severity: 'error' });
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setSnackbar({ open: true, message: '새 비밀번호는 6자 이상이어야 합니다.', severity: 'error' });
      return;
    }
    if (!passwordForm.currentPassword) {
      setSnackbar({ open: true, message: '현재 비밀번호를 입력해주세요.', severity: 'error' });
      return;
    }

    setPasswordLoading(true);
    try {
      const token = await getAccessToken();
      const res = await fetch('/api/v1/parent/auth/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: passwordForm.currentPassword,
          new_password: passwordForm.newPassword,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || '비밀번호 변경에 실패했습니다.');
      }

      setPasswordDialogOpen(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSnackbar({ open: true, message: '비밀번호가 변경되었습니다.', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || '비밀번호 변경에 실패했습니다.', severity: 'error' });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handlePhoneChange = () => {
    // TODO: 실제 전화번호 변경 API 연동
    setPhoneDialogOpen(false);
    setPhoneForm({ newPhone: '', verificationCode: '' });
    setSnackbar({ open: true, message: '전화번호가 변경되었습니다.', severity: 'success' });
  };

  const handleWithdraw = () => {
    // TODO: 실제 회원 탈퇴 API 연동
    setWithdrawDialogOpen(false);
    setSnackbar({ open: true, message: '회원 탈퇴가 완료되었습니다.', severity: 'success' });
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('로그아웃 오류:', error);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        설정
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        알림 및 계정 설정을 관리합니다.
      </Typography>

      {/* 알림 설정 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            알림 설정
          </Typography>
          <List disablePadding>
            <ListItem>
              <ListItemIcon>
                <NotificationsIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="입실/퇴실 알림"
                secondary="자녀가 독서실에 입실하거나 퇴실할 때 알림을 받습니다"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={notifications.entryExit}
                  onChange={() => handleNotificationChange('entryExit')}
                  color="primary"
                />
              </ListItemSecondaryAction>
            </ListItem>
            <Divider variant="inset" component="li" />
            <ListItem>
              <ListItemIcon>
                <PhoneAndroidIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="카카오 알림톡"
                secondary="카카오톡으로 알림 메시지를 받습니다"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={notifications.kakaoAlimtalk}
                  onChange={() => handleNotificationChange('kakaoAlimtalk')}
                  color="primary"
                />
              </ListItemSecondaryAction>
            </ListItem>
            <Divider variant="inset" component="li" />
            <ListItem>
              <ListItemIcon>
                <AccessTimeIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="지각 알림"
                secondary="예정 시간보다 늦게 입실할 경우 알림을 받습니다"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={notifications.lateAlert}
                  onChange={() => handleNotificationChange('lateAlert')}
                  color="primary"
                />
              </ListItemSecondaryAction>
            </ListItem>
            <Divider variant="inset" component="li" />
            <ListItem>
              <ListItemIcon>
                <NotificationsIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary="결석 알림"
                secondary="예정된 날에 입실하지 않을 경우 알림을 받습니다"
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={notifications.absenceAlert}
                  onChange={() => handleNotificationChange('absenceAlert')}
                  color="primary"
                />
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* 계정 관리 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            계정 관리
          </Typography>
          <List disablePadding>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setPasswordDialogOpen(true)}>
                <ListItemIcon>
                  <LockIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="비밀번호 변경"
                  secondary="로그인 비밀번호를 변경합니다"
                />
                <ChevronRightIcon color="action" />
              </ListItemButton>
            </ListItem>
            <Divider variant="inset" component="li" />
            <ListItem disablePadding>
              <ListItemButton onClick={() => setPhoneDialogOpen(true)}>
                <ListItemIcon>
                  <PersonIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="연락처 변경"
                  secondary={parent?.phone || '등록된 전화번호 없음'}
                />
                <ChevronRightIcon color="action" />
              </ListItemButton>
            </ListItem>
            <Divider variant="inset" component="li" />
            <ListItem disablePadding>
              <ListItemButton disabled>
                <ListItemIcon>
                  <ChildCareIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="자녀 연결 관리"
                  secondary="자녀 추가 또는 연결 해제"
                />
                <ChevronRightIcon color="action" />
              </ListItemButton>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* 기타 */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            기타
          </Typography>
          <List disablePadding>
            <ListItem disablePadding>
              <ListItemButton disabled>
                <ListItemIcon>
                  <InfoIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="앱 정보"
                  secondary="버전 1.0.0"
                />
                <ChevronRightIcon color="action" />
              </ListItemButton>
            </ListItem>
            <Divider variant="inset" component="li" />
            <ListItem disablePadding>
              <ListItemButton disabled>
                <ListItemIcon>
                  <HelpIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="고객센터"
                  secondary="문의 및 도움말"
                />
                <ChevronRightIcon color="action" />
              </ListItemButton>
            </ListItem>
            <Divider variant="inset" component="li" />
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon color="warning" />
                </ListItemIcon>
                <ListItemText
                  primary="로그아웃"
                  secondary="현재 기기에서 로그아웃합니다"
                />
                <ChevronRightIcon color="action" />
              </ListItemButton>
            </ListItem>
            <Divider variant="inset" component="li" />
            <ListItem disablePadding>
              <ListItemButton onClick={() => setWithdrawDialogOpen(true)}>
                <ListItemIcon>
                  <DeleteIcon color="error" />
                </ListItemIcon>
                <ListItemText
                  primary="회원 탈퇴"
                  secondary="계정을 삭제하고 탈퇴합니다"
                  primaryTypographyProps={{ color: 'error' }}
                />
                <ChevronRightIcon color="action" />
              </ListItemButton>
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* 비밀번호 변경 다이얼로그 */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)}>
        <DialogTitle>비밀번호 변경</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="현재 비밀번호"
            type="password"
            fullWidth
            variant="outlined"
            value={passwordForm.currentPassword}
            onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="새 비밀번호"
            type="password"
            fullWidth
            variant="outlined"
            value={passwordForm.newPassword}
            onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="새 비밀번호 확인"
            type="password"
            fullWidth
            variant="outlined"
            value={passwordForm.confirmPassword}
            onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)} disabled={passwordLoading}>취소</Button>
          <Button onClick={handlePasswordChange} variant="contained" disabled={passwordLoading}>
            {passwordLoading ? '변경 중...' : '변경'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 전화번호 변경 다이얼로그 */}
      <Dialog open={phoneDialogOpen} onClose={() => setPhoneDialogOpen(false)}>
        <DialogTitle>연락처 변경</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            새로운 전화번호를 입력하고 인증을 완료해주세요.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="새 전화번호"
            type="tel"
            fullWidth
            variant="outlined"
            placeholder="01012345678"
            value={phoneForm.newPhone}
            onChange={(e) => setPhoneForm({ ...phoneForm, newPhone: e.target.value })}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              margin="dense"
              label="인증번호"
              type="text"
              fullWidth
              variant="outlined"
              placeholder="6자리 인증번호"
              value={phoneForm.verificationCode}
              onChange={(e) => setPhoneForm({ ...phoneForm, verificationCode: e.target.value })}
            />
            <Button variant="outlined" sx={{ mt: 1, minWidth: 100 }}>
              인증요청
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhoneDialogOpen(false)}>취소</Button>
          <Button onClick={handlePhoneChange} variant="contained">변경</Button>
        </DialogActions>
      </Dialog>

      {/* 회원 탈퇴 다이얼로그 */}
      <Dialog open={withdrawDialogOpen} onClose={() => setWithdrawDialogOpen(false)}>
        <DialogTitle>회원 탈퇴</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            회원 탈퇴 시 모든 데이터가 삭제되며 복구할 수 없습니다.
          </Alert>
          <DialogContentText>
            정말로 탈퇴하시겠습니까? 탈퇴 후에는 자녀의 출석 기록 조회가 불가능합니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawDialogOpen(false)}>취소</Button>
          <Button onClick={handleWithdraw} color="error" variant="contained">탈퇴</Button>
        </DialogActions>
      </Dialog>

      {/* 스낵바 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
